import { useApiGet } from '@/api/apiRequests';
import { getMetrics } from '@/api/services/getMetrics';
import { createMetric, deleteMetric, getMetricDeletePreview, getMetricOptions, updateMetric } from '@/api/services/metrics';
import { PageLoader } from '@/components/PageLoader';
import type {
  MetricCreatePayload,
  MetricDeleteReport,
  MetricItem,
  MetricOptions,
  MetricWritePayload,
} from '@/interfaces/Metrics';
import { APP_ADMIN_ROLE, hasRole } from '@/utils/roles';
import { AxiosError } from 'axios';
import { EditIcon, Trash2Icon, TriangleAlert } from 'lucide-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

/** Drop-down value for nullable booleans; PrimeReact treats `null` as "nothing selected". */
type TriState = 'true' | 'false' | 'unset';
type BoolChoice = 'true' | 'false' | '';

interface MetricForm {
  metricCode: string;
  description: string;
  unit: string;
  isDescending: BoolChoice;
  isPredictionPossible: TriState;
  standard: number | null;
  isReachingStandardPositive: TriState;
}

const EMPTY_FORM: MetricForm = {
  metricCode: '',
  description: '',
  unit: '',
  isDescending: '',
  isPredictionPossible: 'unset',
  standard: null,
  isReachingStandardPositive: 'unset',
};

const BOOL_OPTIONS: { label: string; value: BoolChoice }[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const TRI_STATE_OPTIONS: { label: string; value: TriState }[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
  { label: 'Not set', value: 'unset' },
];

const toTriState = (value: boolean | null | undefined): TriState => {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return 'unset';
};

const fromTriState = (value: TriState): boolean | null => {
  if (value === 'unset') return null;
  return value === 'true';
};

const formatBool = (value: boolean | null | undefined): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
};

const toForm = (metric: MetricItem): MetricForm => ({
  metricCode: metric.metric_code,
  description: metric.description,
  unit: metric.unit,
  isDescending: metric.is_descending ? 'true' : 'false',
  isPredictionPossible: toTriState(metric.is_prediction_possible),
  standard: metric.standard,
  isReachingStandardPositive: toTriState(metric.is_reaching_standard_positive),
});

const toWritePayload = (form: MetricForm): MetricWritePayload => ({
  description: form.description.trim(),
  unit: form.unit,
  // biome-ignore lint/style/useNamingConvention: backend contract
  is_descending: form.isDescending === 'true',
  // biome-ignore lint/style/useNamingConvention: backend contract
  is_prediction_possible: fromTriState(form.isPredictionPossible),
  standard: form.standard,
  // biome-ignore lint/style/useNamingConvention: backend contract
  is_reaching_standard_positive: fromTriState(form.isReachingStandardPositive),
});

/** Client-side validation mirroring the backend rules; returns the first problem or null. */
const validateForm = (form: MetricForm, isEditMode: boolean, options?: MetricOptions): string | null => {
  if (!isEditMode) {
    if (!form.metricCode.trim()) return 'Metric code is required.';
    if (options?.metric_code_pattern && !new RegExp(options.metric_code_pattern).test(form.metricCode.trim())) {
      return 'Metric code must follow the pattern of existing metrics (e.g. lekari_1000, nabidky_UP_i, migrace_20-34_saldo).';
    }
  }
  if (!form.description.trim()) return 'Description is required.';
  if (!form.unit) return 'Unit is required.';
  if (form.isDescending === '') return 'Choose whether the metric is descending.';
  if ((form.standard === null) !== (form.isReachingStandardPositive === 'unset')) {
    return 'Standard and "Reaching standard is positive" must be either both set or both empty.';
  }
  return null;
};

/** Extract a readable message from an API error (custom {detail: string} or FastAPI validation list). */
const errorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item: { loc?: unknown[]; msg?: string }) => {
        const field = Array.isArray(item.loc) ? item.loc.filter((part) => part !== 'body').join('.') : '';
        return field ? `${field}: ${item.msg ?? ''}` : (item.msg ?? '');
      })
      .join('; ');
  }
  return fallback;
};

interface MetricsSearchProps {
  onChange: (value: string) => void;
  delayMs?: number;
}

/**
 * Search box that keeps keystrokes local and emits the value debounced, so the
 * (expensive) DataTable below re-renders once per pause in typing, not per key.
 */
const MetricsSearch: FC<MetricsSearchProps> = ({ onChange, delayMs = 250 }) => {
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => onChange(value.trim()), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs, onChange]);

  return (
    <InputText
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder="Search metrics"
      aria-label="Search metrics"
      className="w-72"
    />
  );
};

export const Metrics: FC = () => {
  const isAdmin = useMemo(() => hasRole(APP_ADMIN_ROLE), []);
  const [globalFilter, setGlobalFilter] = useState<string>('');

  const [itemDialog, setItemDialog] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [form, setForm] = useState<MetricForm>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [deleteTarget, setDeleteTarget] = useState<MetricItem | null>(null);
  const [deletePreview, setDeletePreview] = useState<MetricDeleteReport | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const {
    data: metrics,
    isLoading,
    refetch,
  } = useApiGet<MetricItem[], AxiosError>(['metrics'], async () => await getMetrics(), { enabled: true });
  const { data: options } = useApiGet<MetricOptions, AxiosError>(['metrics-options'], getMetricOptions, {
    enabled: true,
  });

  const unitOptions = useMemo(() => (options?.units ?? []).map((unit) => ({ label: unit, value: unit })), [options]);
  const rows = useMemo(() => metrics ?? [], [metrics]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setIsEditMode(false);
    setItemDialog(true);
  };

  const openEdit = (metric: MetricItem) => {
    setForm(toForm(metric));
    setIsEditMode(true);
    setItemDialog(true);
  };

  const hideDialog = () => {
    setItemDialog(false);
    setForm(EMPTY_FORM);
    setIsEditMode(false);
  };

  const saveItem = async () => {
    const problem = validateForm(form, isEditMode, options);
    if (problem) {
      toast.error(problem);
      return;
    }

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateMetric(form.metricCode, toWritePayload(form));
        toast.success(`Metric ${form.metricCode} has been updated.`);
      } else {
        const payload: MetricCreatePayload = {
          // biome-ignore lint/style/useNamingConvention: backend contract
          metric_code: form.metricCode.trim(),
          ...toWritePayload(form),
        };
        await createMetric(payload);
        toast.success(`Metric ${payload.metric_code} has been created.`);
      }
      await refetch();
      hideDialog();
    } catch (error) {
      toast.error(`Error: ${errorMessage(error, 'Error when saving the metric.')}`);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async (metric: MetricItem) => {
    setDeleteTarget(metric);
    setDeletePreview(null);
    setIsPreviewLoading(true);
    try {
      setDeletePreview(await getMetricDeletePreview(metric.metric_code));
    } catch (error) {
      toast.error(`Error: ${errorMessage(error, 'Could not load the delete preview.')}`);
      setDeleteTarget(null);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const hideDeleteDialog = () => {
    setDeleteTarget(null);
    setDeletePreview(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget || !deletePreview) return;

    setIsDeleting(true);
    try {
      const report = await deleteMetric(deleteTarget.metric_code);
      const paragraphsNote = report.paragraphs.length
        ? ` and removed from ${report.paragraphs.length} paragraph(s).`
        : '.';
      toast.success(`Metric ${report.metric_code} deleted together with ${report.properties_count} values${paragraphsNote}`);
      await refetch();
      hideDeleteDialog();
    } catch (error) {
      toast.error(`Error: ${errorMessage(error, 'Error when deleting the metric.')}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const actionBodyTemplate = (metric: MetricItem) => (
    <div className="flex gap-1">
      <Button
        icon={<EditIcon size={16} />}
        className="ml-auto p-button-text p-button-info"
        tooltip="Edit"
        tooltipOptions={{ position: 'top' }}
        onClick={() => openEdit(metric)}
      />
      <Button
        icon={<Trash2Icon size={16} />}
        className="ml-auto p-button-text p-button-danger"
        tooltip="Delete"
        tooltipOptions={{ position: 'top' }}
        onClick={() => confirmDelete(metric)}
      />
    </div>
  );

  const itemDialogFooter = (
    <>
      <Button
        className={'btn-secondary'}
        style={{ borderRadius: '6px' }}
        label="Cancel"
        outlined={true}
        onClick={hideDialog}
        disabled={isSaving}
      />
      <Button
        className={'btn-primary'}
        style={{ borderRadius: '6px' }}
        label="Save"
        onClick={saveItem}
        loading={isSaving}
      />
    </>
  );

  const deleteDialogFooter = (
    <>
      <Button
        label="Cancel"
        className={'btn-secondary'}
        style={{ borderRadius: '6px' }}
        onClick={hideDeleteDialog}
        disabled={isDeleting}
      />
      <Button
        label="Delete permanently"
        severity="danger"
        style={{ borderRadius: '6px' }}
        onClick={handleDelete}
        disabled={!deletePreview || isPreviewLoading}
        loading={isDeleting}
      />
    </>
  );

  return (
    <>
      <h1 className={'text-2xl font-bold mb-8 text-white'}>Metrics</h1>
      <div className={'flex justify-between items-center gap-2 mb-2'}>
        <MetricsSearch onChange={setGlobalFilter} />
        {isAdmin && (
          <Button label="Add metric" className={'btn-primary'} style={{ borderRadius: '6px' }} onClick={openNew} />
        )}
      </div>
      <DataTable
        value={rows}
        dataKey="metric_code"
        removableSort={true}
        showGridlines={true}
        paginator={true}
        size={'small'}
        sortField={'metric_code'}
        sortOrder={1}
        rows={20}
        rowsPerPageOptions={[20, 50, 100, 200]}
        globalFilter={globalFilter}
        globalFilterFields={['metric_code', 'description', 'unit']}
        emptyMessage="No metrics found."
        tableStyle={{ minWidth: '60rem', width: '100%' }}
      >
        <Column field="metric_code" header="Code" sortable={true} className={'font-mono text-sm'} />
        <Column field="description" header="Description" sortable={true} />
        <Column field="unit" header="Unit" sortable={true} />
        <Column
          field="is_descending"
          header="Descending"
          sortable={true}
          align={'center'}
          body={(metric: MetricItem) => formatBool(metric.is_descending)}
        />
        <Column
          field="is_prediction_possible"
          header="Prediction"
          sortable={true}
          align={'center'}
          body={(metric: MetricItem) => formatBool(metric.is_prediction_possible)}
        />
        <Column
          field="standard"
          header="Standard"
          sortable={true}
          align={'right'}
          body={(metric: MetricItem) => (metric.standard === null ? '—' : metric.standard)}
        />
        <Column
          field="is_reaching_standard_positive"
          header="Reaching standard is positive"
          sortable={true}
          align={'center'}
          body={(metric: MetricItem) => formatBool(metric.is_reaching_standard_positive)}
        />
        {isAdmin && <Column align={'center'} style={{ width: '6rem' }} body={actionBodyTemplate} />}
      </DataTable>

      <Dialog
        visible={itemDialog}
        style={{ width: '36rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header={isEditMode ? `Update metric ${form.metricCode}` : 'Add new metric'}
        modal={true}
        className="p-fluid"
        footer={itemDialogFooter}
        onHide={hideDialog}
        draggable={false}
      >
        <div className={'flex flex-col gap-4'}>
          <div className="field">
            <label htmlFor="metric_code" className="font-bold">
              Metric code
            </label>
            <InputText
              id="metric_code"
              value={form.metricCode}
              onChange={(e) => setForm((prev) => ({ ...prev, metricCode: e.target.value }))}
              disabled={isEditMode}
              required={true}
              autoFocus={!isEditMode}
              placeholder="e.g. lekari_1000"
              className={'font-mono'}
            />
            {!isEditMode && (
              <small className="text-gray-500">
                Lowercase snake_case stem, optional suffixes separated by "_" or "-" (e.g. lekari_1000, nabidky_UP_i,
                migrace_20-34_saldo). Cannot be changed later.
              </small>
            )}
          </div>
          <div className="field">
            <label htmlFor="description" className="font-bold">
              Description
            </label>
            <InputText
              id="description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              required={true}
              autoFocus={isEditMode}
            />
          </div>
          <div className="field">
            <label htmlFor="unit" className="font-bold">
              Unit
            </label>
            <Dropdown
              id="unit"
              value={form.unit}
              onChange={(e: DropdownChangeEvent) => setForm((prev) => ({ ...prev, unit: e.value }))}
              options={unitOptions}
              optionLabel="label"
              filter={true}
              required={true}
              placeholder="Choose unit"
              className="w-full"
            />
          </div>
          <div className="flex gap-4">
            <div className="field w-full">
              <label htmlFor="is_descending" className="font-bold">
                Descending
              </label>
              <Dropdown
                id="is_descending"
                value={form.isDescending}
                onChange={(e: DropdownChangeEvent) => setForm((prev) => ({ ...prev, isDescending: e.value }))}
                options={BOOL_OPTIONS}
                optionLabel="label"
                required={true}
                placeholder="Choose"
                className="w-full"
              />
            </div>
            <div className="field w-full">
              <label htmlFor="is_prediction_possible" className="font-bold">
                Prediction possible
              </label>
              <Dropdown
                id="is_prediction_possible"
                value={form.isPredictionPossible}
                onChange={(e: DropdownChangeEvent) => setForm((prev) => ({ ...prev, isPredictionPossible: e.value }))}
                options={TRI_STATE_OPTIONS}
                optionLabel="label"
                className="w-full"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="field w-full">
              <label htmlFor="standard" className="font-bold">
                Standard
              </label>
              <InputNumber
                id="standard"
                value={form.standard}
                onValueChange={(e) => setForm((prev) => ({ ...prev, standard: e.value ?? null }))}
                maxFractionDigits={4}
                useGrouping={false}
                placeholder="Not set"
              />
            </div>
            <div className="field w-full">
              <label htmlFor="is_reaching_standard_positive" className="font-bold">
                Reaching standard is positive
              </label>
              <Dropdown
                id="is_reaching_standard_positive"
                value={form.isReachingStandardPositive}
                onChange={(e: DropdownChangeEvent) =>
                  setForm((prev) => ({ ...prev, isReachingStandardPositive: e.value }))
                }
                options={TRI_STATE_OPTIONS}
                optionLabel="label"
                className="w-full"
              />
            </div>
          </div>
          <small className="text-gray-500">
            "Standard" and "Reaching standard is positive" must be either both set or both empty.
          </small>
        </div>
      </Dialog>

      <Dialog
        visible={deleteTarget !== null}
        style={{ width: '32rem' }}
        header="Confirm deletion"
        modal={true}
        draggable={false}
        footer={deleteDialogFooter}
        onHide={hideDeleteDialog}
      >
        {deleteTarget && (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <TriangleAlert size={32} className="text-red-600 shrink-0" />
              <p>
                You are about to delete metric <strong className="font-mono">{deleteTarget.metric_code}</strong> (
                {deleteTarget.description}).
              </p>
            </div>
            {isPreviewLoading && <p className="text-sm text-gray-500">Calculating what will be deleted…</p>}
            {deletePreview && (
              <>
                <ul className="list-disc pl-6">
                  <li>
                    <strong>{deletePreview.properties_count}</strong> stored values (ICO, year) will be deleted.
                  </li>
                  <li>
                    {deletePreview.paragraphs.length === 0 ? (
                      'No paragraph uses this metric.'
                    ) : (
                      <>
                        The metric will be removed from the inputs of <strong>{deletePreview.paragraphs.length}</strong>{' '}
                        paragraph(s):
                        <div className="flex flex-wrap gap-1 mt-1">
                          {deletePreview.paragraphs.map((paragraph) => (
                            <span
                              key={paragraph.id}
                              className="px-2 py-1 font-medium text-xs bg-yellow-100 text-yellow-800 rounded-full"
                            >
                              {paragraph.name} ({paragraph.category})
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </li>
                </ul>
                <p className="font-bold text-red-700">This action is irreversible.</p>
              </>
            )}
          </div>
        )}
      </Dialog>
      <PageLoader visible={isLoading} />
    </>
  );
};
